/**
 * The contract shared by every part of the reinforcement-learning stack.
 *
 * Declared here, ahead of any implementation, so the network, the environment,
 * the trainer and the UI can be built against one another without drifting.
 * Nothing in this file may import Three.js, the DOM, or anything under
 * src/render, src/ui or src/audio: training runs headless in Node worker
 * threads and in browser Web Workers, and `test/sim/decoupling.test.ts` is
 * extended to enforce that for src/rl too.
 *
 * ---------------------------------------------------------------------------
 * THE TWO DESIGN DECISIONS THAT SHAPE EVERYTHING ELSE
 * ---------------------------------------------------------------------------
 *
 * 1. THE ACTION SPACE IS DISCRETE.
 *
 *    Rocket League's controls are eight axes/buttons, which is a continuous
 *    action space. We instead expose a fixed lookup table of useful control
 *    COMBINATIONS and let the policy pick one. This is what RLGym's default
 *    action parser does, and the reason is practical: a categorical policy
 *    needs only a softmax and a log-probability, whereas a continuous policy
 *    needs a squashed Gaussian, an entropy term that depends on the squash,
 *    and per-dimension standard deviations that are easy to get subtly wrong.
 *    Discrete PPO is markedly easier to make CORRECT, and correctness is worth
 *    more here than the last few percent of control resolution.
 *
 * 2. THE POLICY IS A SMALL MLP WE OWN.
 *
 *    No ML framework is added. A dense network with tanh activations, a policy
 *    head and a value head is a few hundred lines, and — critically — its
 *    gradients can be checked against finite differences to 1e-5, which is a
 *    real test rather than a vibe. A framework would be faster to write and
 *    impossible to verify at that granularity in this codebase.
 */

import type { CarInput } from '../physics/car-state';

// ---------------------------------------------------------------------------
// Observations
// ---------------------------------------------------------------------------

/**
 * A single agent's view of the world, already normalized to roughly [-1, 1].
 *
 * Normalization is not cosmetic: an unnormalized observation mixes values
 * spanning 1e-2 (a dot product) and 1e4 (a position in uu) into the same first
 * layer, and the large components dominate the gradient so completely that the
 * small ones are never learned from. Every field must be divided by a stated
 * scale, and `OBS_SCALES` is where those live.
 */
export type Observation = Float32Array;

/**
 * Fixed observation width. Every consumer allocates against this, and the
 * checkpoint records it so a network trained on one layout refuses to load
 * against another rather than silently reading garbage.
 */
export const OBS_SIZE = 70;

/** What the agent is allowed to see. Ordered; the builder must match exactly. */
export interface ObservationSpec {
  /** Field offsets, for tests and for debugging a mislabelled input. */
  readonly fields: readonly { name: string; offset: number; size: number }[];
  readonly size: number;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

/** Index into the action lookup table. */
export type ActionId = number;

/**
 * One entry of the lookup table: a concrete control combination.
 * The table is generated once and must be STABLE — a checkpoint stores policy
 * logits per action index, so reordering the table silently rewires a trained
 * policy's outputs.
 */
export interface ActionEntry {
  readonly input: Readonly<CarInput>;
  /** Human-readable, for the debug overlay ("boost + full left + jump"). */
  readonly label: string;
}

// ---------------------------------------------------------------------------
// The network
// ---------------------------------------------------------------------------

/**
 * Actor-critic MLP. A shared trunk feeds a policy head (logits over the action
 * table) and a value head (one scalar).
 */
export interface PolicyNetwork {
  readonly obsSize: number;
  readonly actionCount: number;
  readonly hiddenSizes: readonly number[];

  /** Logits and value for one observation. Allocation-free where possible. */
  forward(obs: Observation, out: ForwardResult): ForwardResult;

  /** Flat parameter vector, for checkpointing and for gradient checking. */
  getParameters(): Float64Array;
  setParameters(values: Float64Array | readonly number[]): void;
  readonly parameterCount: number;
}

export interface ForwardResult {
  /** Unnormalized scores, one per action. */
  logits: Float64Array;
  /** State-value estimate. */
  value: number;
}

// ---------------------------------------------------------------------------
// Rollouts
// ---------------------------------------------------------------------------

/** One agent-step of experience. */
export interface Transition {
  obs: Observation;
  action: ActionId;
  /** log pi(a|s) under the policy that ACTED. PPO's ratio needs the behaviour
   *  policy's value, not a recomputed one. */
  logProb: number;
  reward: number;
  /** Value estimate at this state, from the acting policy. */
  value: number;
  /**
   * True only when the episode ENDED — a goal. A time limit is NOT a
   * termination: the state after it has a perfectly good value, and the
   * environment stopped for a reason that is not part of the problem.
   *
   * Marking a truncation `done` teaches the critic that every state 60 steps
   * into an episode is worth nothing, which it is not, and — since the
   * observation carries no clock — it cannot even represent the distinction, so
   * it learns the average and the error lands in every advantage. See
   * `bootstrap`.
   */
  done: boolean;
  /**
   * V(s_{t+1}) for a step that ends a trajectory WITHOUT ending the episode: a
   * time limit, or the collection's step budget running out. Absent otherwise,
   * and ignored on a step where `done` is true.
   */
  bootstrap?: number;
}

/** A batch of experience with advantages already computed. */
export interface Batch {
  obs: Float32Array;
  actions: Int32Array;
  logProbs: Float64Array;
  advantages: Float64Array;
  returns: Float64Array;
  oldValues: Float64Array;
  readonly count: number;
}

// ---------------------------------------------------------------------------
// Rewards
// ---------------------------------------------------------------------------

/**
 * A named reward term. Keeping them separate rather than summing eagerly is
 * what makes a reward function debuggable: the UI can chart each contribution
 * and a collapsed policy can be traced to the term that dominated.
 */
export interface RewardTerm {
  readonly name: string;
  /** Weight applied to `compute`. Zero disables the term entirely. */
  weight: number;
  /** Raw, unweighted value for this step. */
  compute(context: RewardContext): number;
}

export interface RewardContext {
  /** Index of the agent being scored. */
  carIndex: number;
  /** The world AFTER the step. */
  readonly world: unknown;
  /** Events emitted by the step that produced this state. */
  readonly events: unknown;
  /** Ticks elapsed in this episode. */
  episodeTick: number;
}

// ---------------------------------------------------------------------------
// Checkpoints
// ---------------------------------------------------------------------------

/**
 * Everything needed to resume training exactly, or to run inference.
 *
 * JSON-safe on purpose: the same object is written to disk by the Node trainer
 * and to IndexedDB by the browser one, and a Float32Array does not survive
 * `JSON.stringify`. `version` gates loading — a checkpoint from an
 * incompatible observation or action layout must be REJECTED, not coerced,
 * because the failure mode otherwise is a policy that looks trained and plays
 * like noise.
 */
export interface Checkpoint {
  version: number;
  /** Guards against loading a policy trained on a different observation. */
  obsSize: number;
  /** Guards against a reordered or resized action table. */
  actionCount: number;
  hiddenSizes: number[];
  /** Flat parameter vector. */
  parameters: number[];
  /** Adam first/second moments, so resuming does not restart the optimizer. */
  optimizer?: { m: number[]; v: number[]; step: number };
  /** Running return-scale estimate, so a resumed run divides rewards by the
   *  same number the straight run did. Absent on checkpoints written before
   *  return normalization existed, and on runs with it disabled. */
  returnScale?: ReturnScaleState;
  stats: TrainingStats;
  /** Free-form label shown in the UI ("run 3, 12M steps"). */
  label?: string;
}

export const CHECKPOINT_VERSION = 1;

/**
 * A running mean/variance estimate of the discounted return. Owned by
 * `ReturnNormalizer` in ./ppo; declared here because a `Checkpoint` carries it
 * and this file is the one place the checkpoint format is written down.
 */
export interface ReturnScaleState {
  mean: number;
  variance: number;
  count: number;
}

/** Progress metrics, appended to as training runs. Drives the live charts. */
export interface TrainingStats {
  /** PPO updates completed. */
  iteration: number;
  /** Environment steps consumed in total, across all resumes. */
  totalSteps: number;
  /** Wall-clock seconds spent training, across all resumes. */
  totalSeconds: number;
  /** One entry per iteration, oldest first. Bounded — see HISTORY_LIMIT. */
  history: IterationStats[];
  /** Where the curriculum had got to, so a resume continues the schedule
   *  instead of restarting it at the easiest states. */
  curriculumDifficulty?: number;
  /** Iterations since the difficulty last ROSE, carried across resumes so a run
   *  restarted every few hours cannot hide a gate that is never met. Optional:
   *  a fixed reset mode has no curriculum, and older checkpoints predate it. */
  curriculumStall?: number;
}

export interface IterationStats {
  iteration: number;
  /** Mean undiscounted episode return over the iteration's rollouts. */
  meanReturn: number;
  /** Mean episode length in ticks. */
  meanEpisodeLength: number;
  /** Goals scored per episode by the learning agent. */
  goalsFor: number;
  goalsAgainst: number;
  /**
   * Fraction of AGENT-episodes with at least one ball touch — the first thing a
   * policy learns, and the clearest early signal that anything is working.
   *
   * Under self-play this has a structural ceiling near 1/nLearners: both cars
   * start beside the ball, one takes it, and the other agent's episode records
   * no touch however well it played. That is why it is charted but NOT what the
   * curriculum gate reads; see `episodeTouchRate`.
   */
  touchRate: number;
  /**
   * Fraction of ENVIRONMENT episodes in which at least one learning agent
   * touched the ball. Ceiling 1.0 for any roster size, which is what makes it
   * usable as a promotion threshold — see `CurriculumConfig` in ../rl/trainer.
   * Optional because history written before the gate was fixed does not have
   * it, and a backend that reports no episode statistics cannot produce it.
   */
  episodeTouchRate?: number;
  /** PPO diagnostics. A collapsing entropy or an exploding KL is the earliest
   *  warning that a run has gone wrong. */
  policyLoss: number;
  valueLoss: number;
  entropy: number;
  approxKl: number;
  clipFraction: number;
  /** Per-term mean reward contribution, for the breakdown chart. */
  rewardTerms: Record<string, number>;
  /** Curriculum position the rollouts were collected at, in [0, 1]. Optional
   *  because a run with a fixed reset mode has no curriculum to report, and
   *  because history written before the curriculum existed does not have it. */
  difficulty?: number;
  /** Iterations the difficulty had been pinned for when this row was written.
   *  Charted beside it so a schedule that is not moving is visible rather than
   *  something a reader has to notice from a flat line. */
  curriculumStall?: number;
}

/** How many iterations of history a checkpoint retains. Unbounded history
 *  makes checkpoints grow without limit over a long run. */
export const HISTORY_LIMIT = 2000;

// ---------------------------------------------------------------------------
// Hyperparameters
// ---------------------------------------------------------------------------

/** PPO configuration. Defaults live in ./ppo and are documented there. */
export interface PPOConfig {
  /** Discount. */
  gamma: number;
  /** GAE lambda. */
  lambda: number;
  /** Surrogate clip range. */
  clipRange: number;
  /** Entropy bonus coefficient — the main lever against premature collapse. */
  entropyCoef: number;
  valueCoef: number;
  maxGradNorm: number;
  learningRate: number;
  /** Passes over each batch. */
  epochs: number;
  minibatchSize: number;
  /**
   * Divide rewards by a running estimate of the discounted return's standard
   * deviation before GAE. Optional so that a `PPOConfig` written before it
   * existed is still a legal one; `DEFAULT_PPO_CONFIG` turns it on and ./ppo
   * documents exactly which scale problem it does and does not solve.
   */
  normalizeReturns?: boolean;
}

/** Everything that defines a training run, so a run is reproducible. */
export interface TrainingConfig {
  ppo: PPOConfig;
  hiddenSizes: number[];
  /** Environment steps collected per PPO update. */
  stepsPerIteration: number;
  /** Parallel environments. In Node this is worker threads. */
  workers: number;
  /** Max ticks before an episode is truncated. */
  episodeTicks: number;
  /** Master seed. Every stochastic choice derives from this. */
  seed: number;
  /** Which reward terms are active, and their weights. */
  rewardWeights: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Training control — the surface the UI and the CLI both drive
// ---------------------------------------------------------------------------

export type TrainerPhase = 'idle' | 'collecting' | 'updating' | 'paused' | 'stopped';

/**
 * Live progress. Emitted after every iteration so a UI can chart it without
 * reaching into the trainer's internals.
 */
export interface TrainerProgress {
  phase: TrainerPhase;
  stats: TrainingStats;
  /** Steps collected so far within the current iteration, for a progress bar. */
  iterationProgress: number;
  stepsPerSecond: number;
}
